// src/pages/Calendar.jsx
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

// Local-date helpers — deliberately NOT using toISOString() for day-keying,
// since that converts to UTC first and silently shifts the "day" a booking
// belongs to for anyone not in UTC (e.g. Nairobi, UTC+3). Everything here
// operates in the browser's own local time, matching what the person
// looking at the calendar actually means by "today".
function pad(n) { return String(n).padStart(2, '0'); }
function localDateKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function isSameDay(a, b) { return localDateKey(a) === localDateKey(b); }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

const STATUS_STYLE = {
  scheduled: 'bg-accent-100 text-accent-600',
  completed: 'bg-sage-100 text-sage-600',
  cancelled: 'bg-ink/5 text-ink/40 line-through',
  no_show: 'bg-p2-100 text-p2',
};

const MODE_ICON = { phone: '📞', video: '🎥', in_person: '📍' };

export default function Calendar() {
  const [cursor, setCursor] = useState(() => new Date());
  const [appointments, setAppointments] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const from = startOfMonth(cursor);
      from.setDate(from.getDate() - 7);
      const to = endOfMonth(cursor);
      to.setDate(to.getDate() + 7);
      const body = await api(`/appointments?from=${from.toISOString()}&to=${to.toISOString()}`);
      setAppointments(body.appointments);
    } catch (err) {
      setError(err.message);
    }
  }, [cursor]);

  useEffect(() => { load(); }, [load]);

  const firstDay = startOfMonth(cursor);
  const lastDay = endOfMonth(cursor);
  const leadingBlanks = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const cells = [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const apptsFor = (day) => day ? appointments.filter((a) => isSameDay(new Date(a.scheduledAt), day)) : [];
  const selectedAppts = apptsFor(selectedDay).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

  async function cancelAppt(id) {
    if (!confirm('Cancel this appointment?')) return;
    try { await api(`/appointments/${id}`, { method: 'DELETE' }); load(); }
    catch (err) { setError(err.message); }
  }

  async function markStatus(id, status) {
    try { await api(`/appointments/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }); load(); }
    catch (err) { setError(err.message); }
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-charcoal">Appointments</h1>
        <button onClick={() => setShowForm(true)} className="rounded-md bg-ink text-white text-sm font-medium px-4 py-2 hover:bg-ink-700">+ Book appointment</button>
      </div>
      {error && <p className="mt-3 text-sm text-p1">{error}</p>}

      <div className="mt-5 grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="text-sm text-ink/50 hover:text-ink px-2">←</button>
            <div className="text-sm font-medium">{cursor.toLocaleDateString([], { month: 'long', year: 'numeric' })}</div>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="text-sm text-ink/50 hover:text-ink px-2">→</button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wide text-ink/40 mb-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              const dayAppts = apptsFor(day).filter((a) => a.status !== 'cancelled');
              const isSelected = day && isSameDay(day, selectedDay);
              const isToday = day && isSameDay(day, new Date());
              return (
                <button
                  key={i}
                  disabled={!day}
                  onClick={() => day && setSelectedDay(day)}
                  className={`h-16 rounded-md border text-left p-1.5 transition-colors ${
                    !day ? 'border-transparent' :
                    isSelected ? 'border-sage bg-sage-100' :
                    isToday ? 'border-accent/40' : 'border-ink/10 hover:bg-paper'
                  }`}
                >
                  {day && (
                    <>
                      <div className={`text-xs ${isToday ? 'font-bold text-accent' : 'text-ink/70'}`}>{day.getDate()}</div>
                      {dayAppts.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-0.5">
                          {dayAppts.slice(0, 4).map((a) => <span key={a.id} className="h-1.5 w-1.5 rounded-full bg-accent" />)}
                        </div>
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink/50 mb-2">
            {selectedDay.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
          </h2>
          <div className="space-y-2">
            {selectedAppts.length === 0 && <p className="text-xs text-ink/40">No appointments this day.</p>}
            {selectedAppts.map((a) => (
              <div key={a.id} className="rounded-lg border border-ink/10 bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-ink/50">
                    {new Date(a.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {a.durationMinutes}min
                  </span>
                  <span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 ${STATUS_STYLE[a.status] ?? ''}`}>{a.status.replace('_', ' ')}</span>
                </div>
                <div className="text-sm font-medium mt-1.5">{a.title}</div>

                {a.clientName && (
                  <div className="text-xs text-ink/70 mt-1.5">
                    <span className="font-medium">{a.clientName}</span>
                    {a.clientPhone && <a href={`tel:${a.clientPhone}`} className="text-accent hover:underline ml-1.5">{a.clientPhone}</a>}
                  </div>
                )}
                {a.caseRef && (
                  <Link to={`/cases/${a.caseId}`} className="text-xs text-accent hover:underline font-mono block mt-0.5">{a.caseRef}</Link>
                )}

                <div className="text-xs text-ink/50 mt-1.5">
                  {MODE_ICON[a.mode]} {a.location || a.mode.replace('_', ' ')}
                </div>
                <div className="text-[11px] text-ink/40 mt-1">
                  With {a.staffName} · booked by {a.createdByName ?? '—'}
                </div>

                {a.status === 'scheduled' && (
                  <div className="flex gap-3 mt-2 pt-2 border-t border-ink/5">
                    <button onClick={() => markStatus(a.id, 'completed')} className="text-[11px] text-sage-600 hover:underline">Mark completed</button>
                    <button onClick={() => markStatus(a.id, 'no_show')} className="text-[11px] text-p2 hover:underline">No-show</button>
                    <button onClick={() => setEditing(a)} className="text-[11px] text-ink/50 hover:text-ink">Reschedule</button>
                    <button onClick={() => cancelAppt(a.id)} className="text-[11px] text-p1 hover:underline">Cancel</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {(showForm || editing) && (
        <BookAppointmentModal
          defaultDate={selectedDay}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BookAppointmentModal({ defaultDate, editing, onClose, onSaved }) {
  const existingLocal = editing ? new Date(editing.scheduledAt) : null;
  const [form, setForm] = useState({
    title: editing?.title ?? '',
    mode: editing?.mode ?? 'phone',
    location: editing?.location ?? '',
    date: existingLocal ? localDateKey(existingLocal) : localDateKey(defaultDate),
    time: existingLocal ? `${pad(existingLocal.getHours())}:${pad(existingLocal.getMinutes())}` : '09:00',
    durationMinutes: editing?.durationMinutes ?? 30,
    notes: editing?.notes ?? '',
    clientName: editing?.clientName ?? '',
    clientPhone: editing?.clientPhone ?? '',
    caseRef: editing?.caseRef ?? '',
  });
  const [resolvedCase, setResolvedCase] = useState(editing?.caseId ? { caseId: editing.caseId, caseRef: editing.caseRef } : null);
  const [linkToCase, setLinkToCase] = useState(!!editing?.caseId);
  const [resolveError, setResolveError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function resolveCaseRef() {
    setResolveError('');
    setResolvedCase(null);
    if (!form.caseRef.trim()) return;
    try {
      const body = await api(`/appointments/resolve-case/${encodeURIComponent(form.caseRef.trim())}`);
      setResolvedCase(body);
    } catch (err) {
      setResolveError(err.message);
    }
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      // Build the moment in the BROWSER'S local time, then let toISOString()
      // convert it to the correct UTC instant — this is what fixes the
      // earlier bug where a naive "YYYY-MM-DD HH:MM" string sent as-is got
      // silently reinterpreted as UTC (a multi-hour shift for anyone not
      // in UTC, e.g. Nairobi).
      const [year, month, day] = form.date.split('-').map(Number);
      const [hour, minute] = form.time.split(':').map(Number);
      const scheduledAt = new Date(year, month - 1, day, hour, minute).toISOString();

      const payload = {
        title: form.title, mode: form.mode, location: form.location || undefined,
        scheduledAt, durationMinutes: Number(form.durationMinutes), notes: form.notes || undefined,
      };
      if (linkToCase && resolvedCase) {
        payload.caseId = resolvedCase.caseId;
      } else {
        payload.clientName = form.clientName || undefined;
        payload.clientPhone = form.clientPhone || undefined;
      }

      if (editing) {
        await api(`/appointments/${editing.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await api('/appointments', { method: 'POST', body: JSON.stringify(payload) });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-40">
      <form onSubmit={submit} className="bg-white rounded-xl p-5 max-w-sm w-full space-y-3 max-h-[90vh] overflow-y-auto">
        <h2 className="font-semibold text-charcoal">{editing ? 'Reschedule appointment' : 'Book appointment'}</h2>

        <input required placeholder="Title / purpose" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />

        <div className="grid grid-cols-2 gap-2">
          <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-md border border-ink/20 px-3 py-2 text-sm" />
          <input required type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-md border border-ink/20 px-3 py-2 text-sm" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="rounded-md border border-ink/20 px-3 py-2 text-sm">
            <option value="phone">Phone</option>
            <option value="video">Video</option>
            <option value="in_person">In person</option>
          </select>
          <input type="number" min={15} step={15} value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} className="rounded-md border border-ink/20 px-3 py-2 text-sm" placeholder="Minutes" />
        </div>

        {form.mode === 'in_person' && (
          <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
        )}

        <div className="border-t border-ink/10 pt-3">
          <div className="flex gap-4 text-xs mb-2">
            <label className="flex items-center gap-1.5"><input type="radio" checked={linkToCase} onChange={() => setLinkToCase(true)} /> Link to a case</label>
            <label className="flex items-center gap-1.5"><input type="radio" checked={!linkToCase} onChange={() => setLinkToCase(false)} /> Standalone (community, internal, etc.)</label>
          </div>

          {linkToCase ? (
            <div>
              <div className="flex gap-2">
                <input
                  placeholder="Case ref, e.g. SAU-2026-0001" value={form.caseRef}
                  onChange={(e) => { setForm({ ...form, caseRef: e.target.value }); setResolvedCase(null); }}
                  className="flex-1 rounded-md border border-ink/20 px-3 py-2 text-sm font-mono"
                />
                <button type="button" onClick={resolveCaseRef} className="text-xs rounded-md border border-ink/20 px-3 hover:bg-ink hover:text-white transition-colors">Find</button>
              </div>
              {resolveError && <p className="text-xs text-p1 mt-1">{resolveError}</p>}
              {resolvedCase && (
                <p className="text-xs text-sage-600 mt-1">✓ {resolvedCase.displayName ?? 'Anonymous'} {resolvedCase.phone ? `· ${resolvedCase.phone}` : ''}</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <input placeholder="Client / attendee name" value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
              <input placeholder="Phone (optional)" value={form.clientPhone} onChange={(e) => setForm({ ...form, clientPhone: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
            </div>
          )}
        </div>

        <textarea placeholder="Notes (optional)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />

        {error && <p className="text-xs text-p1">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 text-sm text-ink/60 py-2">Cancel</button>
          <button type="submit" disabled={busy || (linkToCase && form.caseRef && !resolvedCase)} className="flex-1 rounded-md bg-ink text-white text-sm font-medium py-2 disabled:opacity-50">
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Book'}
          </button>
        </div>
      </form>
    </div>
  );
}
